/**
* @license Apache-2.0
*
* Copyright (c) 2025 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import isObject from '@stdlib/assert-is-plain-object';
import hasOwnProp from '@stdlib/assert-has-own-property';
import { isPrimitive as isBoolean } from '@stdlib/assert-is-boolean';
import { primitives as isIntegerArray } from '@stdlib/assert-is-integer-array';
import isEmptyCollection from '@stdlib/assert-is-empty-collection';
import normalizeIndices from '@stdlib/ndarray-base-to-unique-normalized-indices';
import join from '@stdlib/array-base-join';
import format from '@stdlib/error-tools-fmtprodmsg';


// MAIN //

/**
* Validates function options.
*
* @private
* @param {Object} opts - destination object
* @param {NonNegativeInteger} ndims - number of input ndarray dimensions
* @param {Options} options - function options
* @param {boolean} [options.keepdims] - boolean indicating whether the reduced dimensions should be included in the returned ndarray as singleton dimensions
* @param {IntegerArray} [options.dims] - list of dimensions over which to perform a reduction
* @returns {(Error|null)} null or an error object
*
* @example
* var opts = {};
* var options = {
*     'keepdims': true
* };
* var err = validate( opts, 3, options );
* if ( err ) {
*     throw err;
* }
*/
function validate( opts, ndims, options ) {
	var tmp;
	if ( !isObject( options ) ) {
		return new TypeError( format( '2E12V', options ) );
	}
	if ( hasOwnProp( options, 'keepdims' ) ) {
		opts.keepdims = options.keepdims;
		if ( !isBoolean( opts.keepdims ) ) {
			return new TypeError( format( '2E12o', 'keepdims', opts.keepdims ) );
		}
	}
	if ( hasOwnProp( options, 'dims' ) ) {
		opts.dims = options.dims;
		if ( !isIntegerArray( opts.dims ) && !isEmptyCollection( opts.dims ) ) {
			return new TypeError( format( '2E1Hk', 'dims', opts.dims ) );
		}
		tmp = normalizeIndices( opts.dims, ndims-1 );
		if ( tmp === null ) {
			return new RangeError( format( '2E1Hl', 'dims', join( opts.dims, ',' ) ) );
		}
		if ( tmp.length !== opts.dims.length ) {
			return new Error( format( '2E1Hu', 'dims', join( opts.dims, ',' ) ) );
		}
		opts.dims = tmp;
	}
	return null;
}


// EXPORTS //

export default validate;
